import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Heart, Globe, Users, Building2, GraduationCap, Award, Sparkles, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

const projects = [
  {
    id: 1,
    title: 'Smart Card for Orphans',
    category: 'Social Welfare',
    icon: Heart,
    description: 'Smart card for orphans providing secure and easily accessible identification system. Enables efficient tracking, documentation, and support for welfare and development. Grants access to education, healthcare, and social welfare programs.',
    color: '#ec4899',
  },
  {
    id: 2,
    title: 'Mahaposh Webportal',
    category: 'Digital Platform',
    icon: Globe,
    description: 'Comprehensive web portal for Women and Child Development Department enabling efficient service delivery and program management.',
    color: '#6366f1',
  },
  {
    id: 3,
    title: 'Stree Shakti - EmpowerHer',
    category: 'Women Empowerment',
    icon: Users,
    description: '"Rising Women, Rising Nation" - EmpowerHer is dedicated to fostering personal and professional growth of women through a comprehensive platform. Provides supportive, inclusive, financial, and educational space for women from diverse backgrounds.',
    color: '#8b5cf6',
  },
  {
    id: 4,
    title: 'One Stop Centre',
    category: 'Digital Services',
    icon: Building2,
    description: 'Integrated digital platform for one-stop support services for women, providing access to multiple government schemes and assistance programs.',
    color: '#06b6d4',
  },
  {
    id: 5,
    title: 'Eklavya Digital E-Learning',
    category: 'Education Technology',
    icon: GraduationCap,
    description: 'Digital e-learning platform and studio for educational content delivery, supporting online learning initiatives.',
    color: '#f59e0b',
  },
  {
    id: 6,
    title: 'Yashwini Mahila Bachat Gat',
    category: 'E-Commerce',
    icon: Globe,
    description: 'E-commerce platform for women self-help groups enabling online marketing and sales of products.',
    color: '#10b981',
  },
  {
    id: 7,
    title: 'SJSA Online Hostel Management',
    category: 'Management System',
    icon: Building2,
    description: 'Online hostel management application for students with complete application tracking and management system.',
    color: '#6366f1',
  },
  {
    id: 8,
    title: 'Regional Deputy Commissioner Portal',
    category: 'Government Portal',
    icon: Globe,
    description: 'Official digital website for Regional Deputy Commissioner office, Konkan Bhavan.',
    color: '#8b5cf6',
  },
  {
    id: 9,
    title: 'Asst Commissioner Social Welfare',
    category: 'Government Portal',
    icon: Globe,
    description: 'Official digital website for Assistant Commissioner Social Welfare Department.',
    color: '#06b6d4',
  },
  {
    id: 10,
    title: 'Digital High Tech Library',
    category: 'Education Infrastructure',
    icon: GraduationCap,
    description: 'High-tech digital library at convention centre under Deputy Regional Commissioner Social Welfare Department.',
    color: '#f59e0b',
  },
  {
    id: 11,
    title: 'High Tech Barti',
    category: 'Youth Development',
    icon: Sparkles,
    description: 'Web-based system to assist in planning and implementation of youth group programs for skill development and empowerment.',
    color: '#ec4899',
  },
  {
    id: 12,
    title: 'Yuva Gat Portal',
    category: 'Youth Development',
    icon: Users,
    description: 'Web-based system for youth group program planning and implementation, supporting youth development initiatives.',
    color: '#10b981',
  },
  {
    id: 13,
    title: 'Bharat Sports Inter-University',
    category: 'Sports Management',
    icon: Award,
    description: 'Digital data management system for inter-university sports across all Maharashtra universities.',
    color: '#6366f1',
  },
  {
    id: 14,
    title: 'TB Mukat Bharat Awareness',
    category: 'Health Awareness',
    icon: Heart,
    description: 'Digital awareness platform for tuberculosis eradication program under Government of Maharashtra, Lokbhavan.',
    color: '#ec4899',
  },
  {
    id: 15,
    title: 'Artist Digital Data Management',
    category: 'Cultural Platform',
    icon: Sparkles,
    description: 'Digital data management system for artists, supporting cultural programs and artist database management.',
    color: '#8b5cf6',
  },
  {
    id: 16,
    title: 'University E-Learning Platform',
    category: 'Education Technology',
    icon: GraduationCap,
    description: 'Digital e-learning platform and mobile application for Mumbai city and Mumbai suburban under University of Mumbai.',
    color: '#06b6d4',
  },
]

export const ProjectsShowcase = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-24 relative">
      {/* Background is global in Home.tsx */}
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
            }}
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">Selected Work</span>
            <span className="text-sm font-bold text-primary">({projects.length})</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Government Projects
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Real impact through technology. See how we've transformed government operations with measurable outcomes.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project, index) => {
            const Icon = project.icon
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group relative"
              >
                <div className="rounded-2xl p-6 h-full transition-all duration-300 hover:translate-y-[-2px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* Category Badge & Icon */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-medium px-3 py-1 rounded-full"
                      style={{
                        background: `${project.color}20`,
                        color: project.color,
                        border: `1px solid ${project.color}40`,
                      }}
                    >
                      {project.category}
                    </span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: `${project.color}20`,
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: project.color }} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-400 leading-relaxed text-justify">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <Link
            to="/projects"
            className="btn-glow inline-flex items-center gap-2 px-6 py-3 text-white font-semibold"
          >
            <span>View All Projects</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}